/**
 * DialerDevice.jsx — WebRTC agent workstation. Rendered as the Campaigns tab.
 *
 * Owns the softphone's lifecycle and the agent's view of a dialing session.
 * `status` moves along the transition table in ./lib/callStateMachine.js —
 * see that file for the full state diagram and legal transitions; this
 * component only ever calls `dispatchStatus(event)`, never `setStatus`
 * directly.
 *
 * The connected lead always arrives as an *inbound* call to this browser: the
 * backend bridges the winning outbound leg with `<Dial>`, so the agent never
 * dials — they answer. With auto-answer on, the call is accepted the instant
 * it arrives, which is what keeps the human on the other end from hearing
 * dead air after they say "hello".
 *
 * ── Two softphone stacks behind one component ───────────────────────────────
 * `/api/token`'s response carries `provider` — one source of truth, so this
 * file never needs its own copy of `TELEPHONY_PROVIDER` to drift out of sync
 * with the backend's. Twilio uses `@twilio/voice-sdk`'s `Device`/`Call`
 * object model, built for exactly this (register an identity, get bridged
 * inbound calls). SignalWire has no equivalent SDK object for this shape —
 * see telephonyClient.js#buildAgentDialNoun on the backend for the full
 * reasoning — so the SignalWire path below registers a classic SIP Endpoint
 * over WebSocket with `sip.js`'s `SimpleUser` instead, and `createCallAdapter`
 * wraps it in a Call-shaped facade (`.on()`, `.accept()`, `.mute()`, …) so
 * every call-handling line below this point — `attachCall`, `acceptCall`,
 * `toggleMute`, `hangUp` — runs unchanged for both providers. Only
 * `goOnline`/`goOffline` know which stack is live.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Device } from '@twilio/voice-sdk';
import { SimpleUser } from 'sip.js/lib/platform/web/simple-user/simple-user.js';
import { AgentStatus, transition } from './lib/callStateMachine.js';
import { createSignalwireCallAdapter, attachVolumeMeter } from './lib/signalwireSoftphone.js';
import { DEFAULT_DISPOSITIONS, OUTCOMES } from './lib/outcomes.js';
import NotesModal from './components/NotesModal.jsx';
import SettingsPanel from './components/SettingsPanel.jsx';

export { AgentStatus };

const STATUS_TONE = {
  [AgentStatus.OFFLINE]: 'neutral',
  [AgentStatus.CONNECTING]: 'pending',
  [AgentStatus.READY]: 'good',
  [AgentStatus.RINGING]: 'pending',
  [AgentStatus.IN_CALL]: 'live',
  [AgentStatus.ERROR]: 'bad',
};

const MAX_LOG_ENTRIES = 200;

const LEG_LIFECYCLE_EVENTS = new Set(['leg:dialing', 'leg:streaming', 'leg:classified', 'leg:connected', 'leg:ended']);

/** One line rack tile's label/tone for a leg snapshot off the SSE feed.
 * Mirrors dialerEngine.js's LegState/Disposition exactly — see that file if
 * a new value ever gets added on either enum. */
function legPhase(leg) {
  if (leg.state === 'ENDED') {
    switch (leg.disposition) {
      case 'HUMAN': return { label: 'Ended', tone: 'neutral' };
      case 'MACHINE': return { label: 'Voicemail', tone: 'pending' };
      case 'NO_ANSWER': return { label: 'No answer', tone: 'neutral' };
      case 'BUSY': return { label: 'Busy', tone: 'neutral' };
      case 'FAILED': return { label: 'Failed', tone: 'bad' };
      case 'ABANDONED': return { label: 'Lost race', tone: 'neutral' };
      case 'CANCELED': return { label: 'Canceled', tone: 'neutral' };
      default: return { label: 'Ended', tone: 'neutral' };
    }
  }
  switch (leg.state) {
    case 'QUEUED': return { label: 'Queued', tone: 'neutral' };
    case 'RINGING': return { label: 'Ringing', tone: 'pending' };
    case 'ANSWERED': return { label: 'Answered', tone: 'pending' };
    case 'CLASSIFYING': return { label: 'Listening…', tone: 'accent' };
    case 'CONNECTED': return { label: 'Connected', tone: 'live' };
    default: return { label: leg.state ?? 'Unknown', tone: 'neutral' };
  }
}

/**
 * @param {object} props
 * @param {string} [props.apiBase] backend origin; defaults to same-origin
 * @param {string} [props.identity] client identity, must match the backend's
 * @param {{ text: string, listName: string, contacts?: object[], token: number, autoStart?: boolean }} [props.loadRequest]
 *   set by the parent (e.g. "Start campaign" on the Lists tab) to replace the
 *   dial list; `token` must change on every request so the same list can be
 *   loaded twice in a row. `contacts`, when present, are that list's full
 *   records — carried through to the dialer so a connected call can screen-pop
 *   the company/name instead of just a phone number.
 * @param {object[]} [props.contacts] every contact on file, across lists —
 *   the fallback screen-pop match when a quick-dial number is already known.
 * @param {(entry: { phone: string, disposition: string, at: number }) => void} [props.onLegEnded]
 *   fired for every leg the engine reports ended, win or lose — this is how
 *   Contacts learns a number's last outcome.
 * @param {(state: {online: boolean, busy: boolean}) => void} [props.onStateChange]
 *   reports whether the sidebar's intentional one-number dial action is safe.
 * @param {(summary: object) => void} [props.onSessionEnded] fired once a
 *   session is stopped or exhausts its queue.
 */
export default function DialerDevice({
  apiBase = '',
  identity = 'agent_1',
  loadRequest = null,
  settingsOpen = false,
  onCloseSettings,
  contacts = [],
  onStateChange,
  onLegEnded,
  onSessionEnded,
  onOutcomeSaved,
  theme = 'dark',
  onToggleTheme,
  currentUser = null,
  accountsEnabled = false,
}) {
  const [status, setStatus] = useState(AgentStatus.OFFLINE);
  const [error, setError] = useState(null);
  const [muted, setMuted] = useState(false);
  const [autoAnswer, setAutoAnswer] = useState(true);
  const [pendingCall, setPendingCall] = useState(null);
  const [callInfo, setCallInfo] = useState(null);
  const [lastEndedCall, setLastEndedCall] = useState(null);
  const [inputLevel, setInputLevel] = useState(0);
  const [outputLevel, setOutputLevel] = useState(0);
  const [bridgeMode, setBridgeMode] = useState(false);
  const [savingOutcome, setSavingOutcome] = useState(false);
  const [currentLead, setCurrentLead] = useState(null);
  const [events, setEvents] = useState([]);
  const [autoStartToken, setAutoStartToken] = useState(null);
  const [held, setHeld] = useState(false);
  const [saveError, setSaveError] = useState('');
  // Outcomes offered in the post-call window: custom ones from Supabase, or the
  // built-in outcomes until/unless those load.
  const [dispositions, setDispositions] = useState(DEFAULT_DISPOSITIONS);

  const [leadsText, setLeadsText] = useState('');
  const [activeListName, setActiveListName] = useState(null);
  // The full contact records behind the active list, when it came from
  // "Start campaign" rather than a freeform paste — keyed for startSession
  // to attach contactId/name/company onto each lead below.
  const [activeListContacts, setActiveListContacts] = useState([]);
  const [batchSize, setBatchSize] = useState(4);
  // 'power'  → one line, agent bridged on answer, no AMD verdict acted on.
  // 'parallel' → N lines, the AMD verdict picks who reaches the agent.
  const [mode, setMode] = useState('power');
  const screening = mode === 'parallel';
  const effectiveBatchSize = screening ? Number(batchSize) : 1;
  const [session, setSession] = useState(null);
  const [starting, setStarting] = useState(false);
  const [meetingsBooked, setMeetingsBooked] = useState(0);
  const meetingsBookedRef = useRef(0);
  const sessionMetaRef = useRef(null); // { startedAt, mode, listName, leadsTotal }
  // Read from the SSE handler and finishSession, which close over their first
  // render only — so these live in refs, not state.
  const sessionIdRef = useRef(null);
  const outcomeCountsRef = useRef({});
  const legCountsRef = useRef({});
  const outcomesByCallSidRef = useRef(new Map());
  // Every leg in the current batch, keyed by legId — replaced wholesale on
  // `batch:started`, upserted per leg on every lifecycle event after that.
  // This is what renders the line rack: real state off the same SSE feed
  // the Activity log already reads, not a mock. One leg in power-dial mode
  // (batchSize forced to 1), up to ten in parallel mode.
  const [batchLegs, setBatchLegs] = useState([]);

  const deviceRef = useRef(null);
  const callRef = useRef(null);
  const callTimerRef = useRef(null);
  const callStartRef = useRef(null);
  // SignalWire only: which stack is live, so goOffline/unmount know whether
  // deviceRef holds a Twilio Device (`.destroy()`) or a SIP.js SimpleUser
  // (`.disconnect()`), and the stop function for the volume-meter polling
  // loop attachVolumeMeter() returns — there is no SDK-native volume event
  // to tear down for that path, just a setInterval/AudioContext.
  const providerRef = useRef('twilio');
  const bridgeModeRef = useRef(false);
  const stopVolumeMeterRef = useRef(null);
  // SimpleUser needs a real <audio> element to attach the remote stream to;
  // Twilio's Voice SDK plays remote audio itself and needs no DOM element.
  const remoteAudioRef = useRef(null);
  const [elapsed, setElapsed] = useState(0);
  const [notes, setNotes] = useState('');
  // The AMD-phase snippet for whichever leg most recently won — captured off
  // the SSE feed below, snapshotted onto the call the moment it connects.
  // This is NOT a live conversation transcript: the Deepgram socket closes
  // the instant a verdict is reached (see AGENTS.md invariant 6), so nothing
  // is heard past that point. Best-effort under parallel dial, where more
  // than one leg can classify HUMAN in close succession — exact under power
  // dial, the one call at a time case this is mainly for.
  const preConnectTranscriptRef = useRef('');
  // The winning leg's identity — outbound Twilio Call SID, the lead's own
  // phone number, and (when known) their name/company — captured at the
  // same `leg:classified` moment as the transcript above, for the same
  // best-effort-under-parallel-dial reason.
  //
  // `callSid` is NOT `call.parameters.CallSid`: that's the inbound
  // `<Dial><Client>` leg to this browser, a different call from the
  // backend's point of view, and it's the outbound leg's SID that
  // `logCallActivity` used when it created this call's HubSpot engagement —
  // so it's the only id `logOutcome` below can use to find that same
  // engagement again.
  //
  // `phone` is NOT `call.parameters.From` either: the bridge in
  // dialerEngine.js's `#connectToAgent` sets no `callerId` on `<Dial>`, so
  // Twilio's default applies and the inbound leg's From is the *parent*
  // call's From — this agency's own Twilio caller ID, not the lead's number.
  // The SSE feed is the only place the lead's actual number is available on
  // this side of the bridge.
  const connectedLegRef = useRef(null);

  // A list handed over from the Lists tab replaces the loaded queue. `token`
  // changes on every request so re-sending the same list still applies.
  useEffect(() => {
    if (!loadRequest) return;
    setLeadsText(loadRequest.text);
    setActiveListName(loadRequest.listName);
    setActiveListContacts(loadRequest.contacts ?? []);
    setAutoStartToken(loadRequest.autoStart ? loadRequest.token : null);
  }, [loadRequest]);

  // phone (E.164) → contact record, for attaching name/company to a lead
  // before dialing and for screen-popping a connected call. The active
  // list's own records win; contacts on file elsewhere are the fallback.
  const phoneToContact = useMemo(() => {
    const map = new Map();
    for (const c of contacts) if (c.phone1) map.set(c.phone1, c);
    for (const c of activeListContacts) if (c.phone1) map.set(c.phone1, c);
    return map;
  }, [contacts, activeListContacts]);

  // The SSE effect below closes over this once and never resubscribes (a
  // deliberate choice — see its own comment), so it reads through a ref
  // instead of the memo directly to stay current without dropping frames.
  const phoneToContactRef = useRef(phoneToContact);
  useEffect(() => {
    phoneToContactRef.current = phoneToContact;
  }, [phoneToContact]);

  // The browser session cookie is sent automatically on same-origin requests.
  const authHeaders = useMemo(() => ({}), []);

  const loadDispositions = useCallback(() => {
    fetch(`${apiBase}/api/dispositions`, { headers: authHeaders })
      .then((res) => (res.ok ? res.json() : null))
      .then((rows) => {
        if (Array.isArray(rows) && rows.length > 0) setDispositions(rows);
      })
      .catch(() => { /* keep whatever is already loaded */ });
  }, [apiBase, authHeaders]);

  // On mount, and again whenever Settings closes (outcomes may have changed).
  useEffect(() => {
    if (!settingsOpen) loadDispositions();
  }, [settingsOpen, loadDispositions]);

  /** Append to the activity log, newest first, bounded. */
  const pushEvent = useCallback((level, message, detail) => {
    setEvents((prev) =>
      [{ id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, at: new Date(), level, message, detail }, ...prev].slice(
        0,
        MAX_LOG_ENTRIES,
      ),
    );
  }, []);

  /** Move `status` along the transition table in ./lib/callStateMachine.js.
   * Never throws from inside an SDK event handler: an event that isn't legal
   * from the current state is left alone (with a dev warning) rather than
   * guessed at. */
  const dispatchStatus = useCallback((event) => {
    setStatus((current) => {
      const next = transition(current, event);
      if (next === null) {
        console.warn(`[DialerDevice] ignored ${event} while ${current}`);
        return current;
      }
      return next;
    });
  }, []);

  /**
   * Returns the full `/api/token` payload, not just the token string — the
   * SignalWire path needs `identity` (the SIP Endpoint username, which may
   * not equal the `identity` prop — see signalwireClient.js#createVoiceAccessToken),
   * `sipDomain` and `wsServer` alongside it, and `provider` is how goOnline()
   * decides which softphone stack to boot without its own copy of
   * `TELEPHONY_PROVIDER`.
   */
  const fetchToken = useCallback(async () => {
    const response = await fetch(`${apiBase}/api/token?identity=${encodeURIComponent(identity)}`, {
      headers: authHeaders,
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.message || `Token request failed (${response.status})`);
    }
    return response.json();
  }, [apiBase, identity, authHeaders]);

  // ───────────────────────────────────────────────────── call attachment ────

  /** Wire the per-call listeners that drive the UI while a lead is connected. */
  const attachCall = useCallback(
    (call) => {
      callRef.current = call;

      call.on('accept', () => {
        dispatchStatus('ACCEPTED');
        setPendingCall(null);
        setLastEndedCall(null);
        setNotes('');
        setMuted(call.isMuted());
        callStartRef.current = Date.now();
        const lead = connectedLegRef.current;
        setCallInfo({
          // The lead's actual number (see connectedLegRef's comment) —
          // call.parameters.From is this agency's own Twilio caller ID, not
          // the lead's, so it's only the fallback when the SSE feed missed it.
          from: lead?.phone || call.parameters?.From || 'unknown',
          name: lead?.name || null,
          company: lead?.company || null,
          // The outbound leg's SID (see connectedLegRef's comment) — what
          // logOutcome needs, not the browser leg's own CallSid.
          callSid: lead?.callSid ?? null,
          companyUrl: phoneToContactRef.current.get(lead?.phone)?.companyUrl ?? null,
          startedAt: Date.now(),
          transcript: preConnectTranscriptRef.current,
        });
        preConnectTranscriptRef.current = '';
        connectedLegRef.current = null;
        setElapsed(0);
        callTimerRef.current = setInterval(() => setElapsed((seconds) => seconds + 1), 1000);
        pushEvent('success', 'Call connected', call.parameters?.From);
      });

      const teardown = (label) => {
        if (callTimerRef.current) {
          clearInterval(callTimerRef.current);
          callTimerRef.current = null;
        }
        const durationSeconds = callStartRef.current ? Math.round((Date.now() - callStartRef.current) / 1000) : 0;
        callStartRef.current = null;
        callRef.current = null;
        setPendingCall(null);
        setInputLevel(0);
        setOutputLevel(0);
        setMuted(false);
        setHeld(false);
        if (remoteAudioRef.current) remoteAudioRef.current.muted = false;
        dispatchStatus('CALL_ENDED');
        pushEvent('info', label);
        // A connected call is worth asking about; one that never connected
        // (canceled/rejected before answer) is not. This fires the same way
        // whether the agent hung up or the caller did — Twilio's `disconnect`
        // event doesn't distinguish, and neither does this.
        setCallInfo((info) => {
          if (info) {
            setLastEndedCall({
              from: info.from,
              name: info.name,
              company: info.company,
              companyUrl: info.companyUrl,
              callSid: info.callSid,
              endedAt: Date.now(),
              durationSeconds,
              transcript: info.transcript,
            });
          }
          return null;
        });
      };

      call.on('disconnect', () => teardown('Call ended'));
      call.on('cancel', () => teardown('Call canceled before answer'));
      call.on('reject', () => teardown('Call rejected'));

      call.on('error', (err) => {
        pushEvent('error', `Call error: ${err.message}`, err.code);
        teardown('Call ended after error');
      });

      // Live mic / speaker levels, ~50 Hz. Purely visual — cheap enough to
      // render because both values are clamped to one decimal place.
      call.on('volume', (input, output) => {
        setInputLevel(Math.round(input * 100) / 100);
        setOutputLevel(Math.round(output * 100) / 100);
      });
    },
    [pushEvent, dispatchStatus],
  );

  // ─────────────────────────────────────────────────── device lifecycle ────

  /** The incoming-call wiring is identical once `call` is Call-shaped —
   * shared by both bootTwilioDevice and bootSignalwireUser below. */
  const handleIncomingCall = useCallback(
    (call, fromLabel) => {
      pushEvent('info', `Incoming lead from ${fromLabel ?? 'unknown'}`);
      attachCall(call);

      if (autoAnswerRef.current) {
        call.accept();
      } else {
        setPendingCall(call);
        dispatchStatus('INCOMING');
      }
    },
    [attachCall, pushEvent, dispatchStatus],
  );

  /** Twilio Voice SDK Device — unchanged from before the SignalWire branch existed. */
  const bootTwilioDevice = useCallback(
    async (initialPayload) => {
      const device = new Device(initialPayload.token, {
        // Opus for the browser hop; pcmu keeps parity with the 8 kHz PSTN leg
        // when Opus is unavailable.
        codecPreferences: ['opus', 'pcmu'],
        // Pre-warming the media connection removes the ICE handshake from the
        // critical path when a lead is bridged in.
        maxCallSignalingTimeoutMs: 30000,
        enableImprovedSignalingErrorPrecision: true,
        logLevel: 'error',
      });

      device.on('registered', () => {
        dispatchStatus('REGISTERED');
        pushEvent('success', `Registered as ${identity}`);
      });

      device.on('unregistered', () => {
        dispatchStatus('UNREGISTERED');
        pushEvent('warn', 'Device unregistered');
      });

      device.on('error', (err) => {
        setError(err.message);
        dispatchStatus('DEVICE_ERROR');
        pushEvent('error', `Device error: ${err.message}`, err.code);
      });

      // Tokens are short-lived; refresh before expiry so an in-progress
      // conversation is never dropped by an auth failure. SignalWire's SIP
      // REGISTER refresh is handled internally by SIP.js's Registerer —
      // there is no equivalent event to wire up on that path.
      device.on('tokenWillExpire', async () => {
        try {
          const fresh = await fetchToken();
          device.updateToken(fresh.token);
          pushEvent('info', 'Access token refreshed');
        } catch (err) {
          pushEvent('error', `Token refresh failed: ${err.message}`);
        }
      });

      device.on('incoming', (call) => handleIncomingCall(call, call.parameters?.From));

      deviceRef.current = device;
      providerRef.current = 'twilio';
      await device.register();
    },
    [fetchToken, identity, pushEvent, dispatchStatus, handleIncomingCall],
  );

  /**
   * SignalWire path: a SIP.js `SimpleUser` registered to a classic SignalWire
   * SIP Endpoint over WebSocket. See this file's top comment and
   * lib/signalwireSoftphone.js for why this exists instead of a
   * Twilio-Device-shaped SignalWire SDK call.
   */
  const bootSignalwireUser = useCallback(
    async (payload) => {
      if (!payload.wsServer || !payload.sipDomain) {
        throw new Error('SignalWire token response is missing wsServer/sipDomain');
      }

      const aor = `sip:${payload.identity}@${payload.sipDomain}`;
      let call = null;

      const user = new SimpleUser(payload.wsServer, {
        aor,
        media: { remote: { audio: remoteAudioRef.current ?? undefined } },
        userAgentOptions: {
          authorizationUsername: payload.identity,
          authorizationPassword: payload.token,
        },
        delegate: {
          onServerConnect: () => {
            pushEvent('info', 'Connected to SignalWire');
          },
          onServerDisconnect: (err) => {
            if (err) {
              setError(err.message ?? String(err));
              dispatchStatus('DEVICE_ERROR');
              pushEvent('error', `SignalWire connection lost: ${err.message ?? err}`);
            }
          },
          onRegistered: () => {
            dispatchStatus('REGISTERED');
            pushEvent('success', `Registered as ${payload.identity} (SIP)`);
          },
          onUnregistered: () => {
            dispatchStatus('UNREGISTERED');
            pushEvent('warn', 'SIP registration ended');
          },
          onCallReceived: () => {
            call = createSignalwireCallAdapter(user);
            handleIncomingCall(call, null);
          },
          onCallAnswered: () => {
            call?.emit('accept');
            stopVolumeMeterRef.current = attachVolumeMeter(user, (input, output) => {
              call?.emit('volume', input, output);
            });
          },
          onCallHangup: () => {
            stopVolumeMeterRef.current?.();
            stopVolumeMeterRef.current = null;
            call?.emit('disconnect');
            call = null;
          },
        },
      });

      deviceRef.current = user;
      providerRef.current = 'signalwire';

      await user.connect();
      await user.register();
    },
    [handleIncomingCall, pushEvent, dispatchStatus],
  );

  const goOnline = useCallback(async () => {
    if (deviceRef.current) return;

    setError(null);
    dispatchStatus('CONNECT');
    pushEvent('info', 'Requesting access token…');

    try {
      const payload = await fetchToken();

      if (payload.phoneBridge) {
        setBridgeMode(true);
        bridgeModeRef.current = true;
        providerRef.current = 'signalwire';
        deviceRef.current = { _phoneBridge: true };
        dispatchStatus('REGISTERED');
        pushEvent('success', `Phone-bridge mode — calls ring ${payload.identity}'s phone`);
      } else if (payload.provider === 'signalwire') {
        setBridgeMode(false);
        bridgeModeRef.current = false;
        await bootSignalwireUser(payload);
      } else {
        setBridgeMode(false);
        bridgeModeRef.current = false;
        await bootTwilioDevice(payload);
      }
    } catch (err) {
      setError(err.message);
      dispatchStatus('DEVICE_ERROR');
      pushEvent('error', `Failed to go online: ${err.message}`);
      deviceRef.current = null;
    }
  }, [fetchToken, bootTwilioDevice, bootSignalwireUser, pushEvent, dispatchStatus]);

  const goOffline = useCallback(() => {
    callRef.current?.disconnect();
    stopVolumeMeterRef.current?.();
    stopVolumeMeterRef.current = null;
    if (deviceRef.current?._phoneBridge) {
      // Phone-bridge stub — nothing to tear down.
    } else if (providerRef.current === 'signalwire') {
      deviceRef.current?.unregister().catch(() => {});
      deviceRef.current?.disconnect().catch(() => {});
    } else {
      deviceRef.current?.destroy();
    }
    deviceRef.current = null;
    callRef.current = null;
    dispatchStatus('GO_OFFLINE');
    setPendingCall(null);
    setCallInfo(null);
    setBridgeMode(false);
    bridgeModeRef.current = false;
    pushEvent('info', 'Went offline');
  }, [pushEvent, dispatchStatus]);

  // The `incoming` listener closes over `autoAnswer`; a ref keeps it current
  // without tearing down and re-registering the Device on every toggle.
  const autoAnswerRef = useRef(autoAnswer);
  useEffect(() => {
    autoAnswerRef.current = autoAnswer;
  }, [autoAnswer]);

  // Release the Device/SIP session on unmount so the browser releases the
  // microphone either way.
  useEffect(
    () => () => {
      if (callTimerRef.current) clearInterval(callTimerRef.current);
      stopVolumeMeterRef.current?.();
      callRef.current?.disconnect();
      if (providerRef.current === 'signalwire') {
        deviceRef.current?.disconnect().catch(() => {});
      } else {
        deviceRef.current?.destroy();
      }
    },
    [],
  );

  // ───────────────────────────────────────────────────── call controls ────

  const acceptCall = useCallback(() => {
    pendingCall?.accept();
  }, [pendingCall]);

  const rejectCall = useCallback(() => {
    pendingCall?.reject();
    setPendingCall(null);
    dispatchStatus('CALL_ENDED');
  }, [pendingCall, dispatchStatus]);

  const toggleMute = useCallback(() => {
    const call = callRef.current;
    if (!call) return;
    const next = !call.isMuted();
    call.mute(next);
    setMuted(next);
    pushEvent('info', next ? 'Microphone muted' : 'Microphone unmuted');
  }, [pushEvent]);

  const hangUp = useCallback(() => {
    callRef.current?.disconnect();
    pushEvent('info', 'Hang up requested');
  }, [pushEvent]);

  /** Hold = mic muted and the far end silenced locally. The Twilio Voice SDK
   * can't mute incoming audio, so on that path only the mic is muted. Not
   * offered in phone-bridge mode: the phone, not this browser, carries audio. */
  const toggleHold = useCallback(() => {
    const call = callRef.current;
    if (!call) return;
    const next = !held;
    call.mute(next);
    setMuted(next);
    if (remoteAudioRef.current) remoteAudioRef.current.muted = next;
    setHeld(next);
    pushEvent('info', next ? 'Call on hold' : 'Call taken off hold');
  }, [held, pushEvent]);

  /** Pause/unpause the dialing queue. Server-side: a live call is never
   * touched, the next call just isn't placed (see AGENTS.md). */
  const togglePause = useCallback(async () => {
    if (!session?.sessionId) return;
    const action = session.holdRequested ? 'unpause' : 'pause';
    try {
      const response = await fetch(`${apiBase}/api/sessions/${encodeURIComponent(session.sessionId)}/${action}`, {
        method: 'POST', headers: authHeaders,
      });
      if (!response.ok) throw new Error(`${action} failed (${response.status})`);
      const snapshot = await response.json();
      if (snapshot.state !== 'IDLE') setSession(snapshot);
      pushEvent('info', action === 'pause' ? 'Session will hold after this call' : 'Session pause cleared');
    } catch (err) {
      pushEvent('error', err.message);
    }
  }, [session, apiBase, authHeaders, pushEvent]);

  const logOutcome = useCallback(
    async (outcomeKey, { notes: notesOverride, label: labelOverride } = {}) => {
      const label = labelOverride ?? OUTCOMES.find((o) => o.key === outcomeKey)?.label ?? outcomeKey;
      const noteText = notesOverride ?? notes;
      const inCall = Boolean(callInfo);
      const active = inCall ? callInfo : lastEndedCall;
      if (!active?.callSid) {
        const message = 'Cannot save this call: the carrier call ID is missing. Stop dialing and check the activity log.';
        pushEvent('error', message);
        setSaveError(message);
        return;
      }
      setSaveError('');
      setSavingOutcome(true);
      try {
        const response = await fetch(`${apiBase}/api/workspace/outcomes`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders },
          body: JSON.stringify({ callSid: active.callSid, outcome: outcomeKey, label, notes: noteText,
            phone: active.from, durationSeconds: inCall ? elapsed : active.durationSeconds,
            connectedAt: active.startedAt }),
        });
        if (!response.ok) throw new Error(`save failed (${response.status})`);
        const result = await response.json();
        if (result.created) {
          onOutcomeSaved?.({ callSid: active.callSid, outcome: outcomeKey, notes: noteText, phone: active.from, savedAt: Date.now() });
          outcomeCountsRef.current[outcomeKey] = (outcomeCountsRef.current[outcomeKey] ?? 0) + 1;
          outcomesByCallSidRef.current.set(active.callSid, { outcome: outcomeKey, notes: noteText });
        }
        if (result.created && outcomeKey === 'meeting') {
          meetingsBookedRef.current += 1;
          setMeetingsBooked(meetingsBookedRef.current);
        }
        pushEvent('success', `Saved: ${label}`, [active.from, noteText].filter(Boolean).join(' — '));
        if (!inCall && session?.sessionId) {
          const resumed = await fetch(`${apiBase}/api/sessions/${encodeURIComponent(session.sessionId)}/resume`, { method: 'POST' });
          if (!resumed.ok) throw new Error(`saved, but could not continue (${resumed.status})`);
          const snapshot = await resumed.json();
          if (snapshot.state !== 'IDLE') setSession(snapshot);
        }
        if (!inCall) setLastEndedCall(null);
        setNotes('');
      } catch (err) {
        pushEvent('error', err.message);
        setSaveError(err.message);
      } finally {
        setSavingOutcome(false);
      }
    },
    [apiBase, authHeaders, pushEvent, callInfo, lastEndedCall, notes, session, elapsed, onOutcomeSaved],
  );

  // ──────────────────────────────────────────────────── session control ────

  const finishSession = useCallback(
    (finalSnapshot) => {
      const meta = sessionMetaRef.current;
      if (meta && onSessionEnded) {
        onSessionEnded({
          id: `session-${meta.startedAt}`,
          startedAt: meta.startedAt,
          endedAt: Date.now(),
          mode: meta.mode,
          listName: meta.listName,
          leadsTotal: meta.leadsTotal,
          durationSeconds: Math.round((Date.now() - meta.startedAt) / 1000),
          stats: { ...(finalSnapshot?.stats ?? {}), busy: legCountsRef.current.BUSY ?? 0 },
          meetingsBooked: meetingsBookedRef.current,
          // Saved outcomes by key — what the Make webhook and the session
          // report break the conversations down by.
          outcomes: {
            meeting: 0, callback: 0, meaningful: 0, email: 0, not_interested: 0, bad_data: 0, do_not_call: 0,
            ...outcomeCountsRef.current,
          },
        });
      }
      sessionMetaRef.current = null;
      sessionIdRef.current = null;
      outcomeCountsRef.current = {};
      legCountsRef.current = {};
      outcomesByCallSidRef.current = new Map();
      setHeld(false);
      setSession(null);
      setMeetingsBooked(0);
      meetingsBookedRef.current = 0;
      setBatchLegs([]);
      setCurrentLead(null);
      setLeadsText('');
      setActiveListName(null);
      setActiveListContacts([]);
    },
    [onSessionEnded],
  );

  const startSession = useCallback(async () => {
    const phones = leadsText
      .split(/[\n,;]+/)
      .map((entry) => entry.trim())
      .filter(Boolean);

    // Attach name/company when this number matches a known contact —
    // dialerEngine.js carries these through to every SSE event, which is
    // what powers the screen-pop below. Deliberately NOT contactId: our
    // local contact record's `id` is this workspace's own
    // `crypto.randomUUID()`, not a HubSpot object id, and
    // hubspotService.js#logCallActivity does `args.contactId ??
    // findContactIdByPhone(phone)` — a truthy-but-wrong id here would skip
    // that working phone lookup and send HubSpot a garbage association.
    const leads = phones.map((phone) => {
      const contact = phoneToContact.get(phone);
      if (!contact) return phone;
      return {
        phone,
        name: [contact.firstName, contact.lastName].filter(Boolean).join(' ') || null,
        company: contact.company || null,
      };
    });

    if (leads.length === 0) {
      pushEvent('warn', 'Add at least one E.164 number before dialing');
      return;
    }

    setStarting(true);
    try {
      const response = await fetch(`${apiBase}/api/sessions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          agentIdentity: identity, leads, batchSize: effectiveBatchSize, screening,
        }),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.message || body.error || 'Failed to start session');

      sessionMetaRef.current = { startedAt: Date.now(), mode, listName: activeListName, leadsTotal: leads.length };
      sessionIdRef.current = body.sessionId;
      outcomeCountsRef.current = {};
      legCountsRef.current = {};
      outcomesByCallSidRef.current = new Map();
      setMeetingsBooked(0);
      meetingsBookedRef.current = 0;
      setBatchLegs([]);
      setSession(body);
      pushEvent(
        'success',
        screening
          ? `Dialing ${leads.length} leads, ${effectiveBatchSize} lines at a time`
          : `Power dialing ${leads.length} leads, one line at a time`,
      );
    } catch (err) {
      pushEvent('error', `Could not start dialing: ${err.message}`);
    } finally {
      setStarting(false);
    }
  }, [leadsText, phoneToContact, effectiveBatchSize, screening, apiBase, authHeaders, identity, pushEvent, activeListName, mode]);

  useEffect(() => {
    if (!autoStartToken || autoStartToken !== loadRequest?.token) return;
    setAutoStartToken(null);
    const ready = status === AgentStatus.READY;
    if (!ready || session || starting) {
      pushEvent('warn', ready ? 'Finish the active session before dialing another number' : 'Go online before dialing');
      return;
    }
    startSession();
  }, [autoStartToken, loadRequest, status, session, starting, startSession, pushEvent]);

  const stopSession = useCallback(async () => {
    if (!session?.sessionId) return;
    try {
      await fetch(`${apiBase}/api/sessions/${session.sessionId}`, { method: 'DELETE', headers: authHeaders });
      pushEvent('warn', 'Dialing session stopped');
      finishSession(session);
    } catch (err) {
      pushEvent('error', `Could not stop session: ${err.message}`);
    }
  }, [session, apiBase, authHeaders, pushEvent, finishSession]);

  // ─────────────────────────────────────────── backend activity feed (SSE) ────

  useEffect(() => {
    const url = new URL(`${apiBase || window.location.origin}/api/events`);
    // EventSource sends the same-origin HttpOnly operator session cookie.
    const source = new EventSource(url.toString());

    /** @type {[string, (payload: any) => string][]} */
    const handlers = [
      ['batch:started', (p) => `Batch dialing ${p.legs?.length ?? 0} lines`],
      ['leg:dialing', (p) => `Ringing ${p.phone}`],
      ['leg:streaming', (p) => `Listening to ${p.phone}`],
      ['leg:classified', (p) => `${p.phone} → ${p.classification}${p.won === false ? ' (lost race)' : ''}`],
      ['leg:connected', (p) => `Bridging ${p.phone} to you`],
      ['leg:ended', (p) => `${p.phone} ended: ${p.disposition ?? 'unknown'}`],
      ['batch:resolved', () => 'Batch resolved'],
      ['session:exhausted', () => 'Lead list exhausted'],
      ['session:paused', (p) => (p.reviewPending ? 'Call ended — save a disposition to continue' : 'Session paused')],
      ['session:hold', (p) => (p.holdRequested ? 'Pause requested — holding after this call' : 'Pause cleared')],
    ];

    const bound = handlers.map(([name, format]) => {
      const listener = (message) => {
        try {
          const payload = JSON.parse(message.data);
          const level = name === 'leg:connected' ? 'success' : name === 'leg:ended' ? 'muted' : 'info';
          pushEvent(level, format(payload), payload.transcript || undefined);
          if (name === 'session:paused' || name === 'session:hold') setSession(payload);
          if (name === 'leg:ended') setCurrentLead((lead) => lead?.legId === payload.legId ? null : lead);
          if (name === 'leg:dialing' || name === 'leg:connected') {
            const contact = phoneToContactRef.current.get(payload.phone);
            setCurrentLead({ ...payload, name: payload.name || [contact?.firstName, contact?.lastName].filter(Boolean).join(' '),
              company: payload.company || contact?.company, companyUrl: contact?.companyUrl, title: contact?.title, email: contact?.email });
          }
          if (name === 'leg:connected' && !bridgeModeRef.current) {
            const contact = phoneToContactRef.current.get(payload.phone);
            connectedLegRef.current = { callSid: payload.callSid, phone: payload.phone,
              name: payload.name || [contact?.firstName, contact?.lastName].filter(Boolean).join(' '),
              company: payload.company || contact?.company };
          }
          if (bridgeModeRef.current && name === 'leg:connected') {
            const contact = phoneToContactRef.current.get(payload.phone);
            const startedAt = payload.connectedAt || Date.now();
            setCallInfo({ from: payload.phone, callSid: payload.callSid, name: payload.name || [contact?.firstName, contact?.lastName].filter(Boolean).join(' '),
              company: payload.company || contact?.company, companyUrl: contact?.companyUrl, startedAt, transcript: payload.transcript });
            setLastEndedCall(null);
            setElapsed(0);
            if (callTimerRef.current) clearInterval(callTimerRef.current);
            callTimerRef.current = setInterval(() => setElapsed(Math.floor((Date.now() - startedAt) / 1000)), 1000);
          }
          if (bridgeModeRef.current && name === 'leg:ended' && payload.callSid) {
            if (callTimerRef.current) clearInterval(callTimerRef.current);
            callTimerRef.current = null;
            setCallInfo((info) => {
              if (info?.callSid === payload.callSid) {
                setLastEndedCall({ ...info, endedAt: Date.now(), durationSeconds: payload.durationSeconds || Math.round((Date.now() - info.startedAt) / 1000) });
                return null;
              }
              return info;
            });
          }
          if (name === 'leg:classified' && payload.classification === 'HUMAN' && payload.won !== false) {
            // Held until the matching call actually rings this browser (see
            // `call.on('accept')`), then cleared. Best-effort under parallel
            // dial — see each ref's own comment for why.
            preConnectTranscriptRef.current = payload.transcript ?? '';
            // name/company normally arrive from the backend (threaded through
            // since startSession attached them) — the local lookup is only a
            // fallback for a session started before that leg carried them.
            const fallback = payload.phone ? phoneToContactRef.current.get(payload.phone) : null;
            connectedLegRef.current = {
              callSid: payload.callSid ?? null,
              phone: payload.phone ?? null,
              name: payload.name || (fallback ? [fallback.firstName, fallback.lastName].filter(Boolean).join(' ') : null) || null,
              company: payload.company || fallback?.company || null,
            };
          }
          if (name === 'leg:ended' && payload.sessionId && payload.sessionId === sessionIdRef.current) {
            const verdict = payload.disposition ?? 'UNKNOWN';
            legCountsRef.current[verdict] = (legCountsRef.current[verdict] ?? 0) + 1;
            // Best-effort call log: a harmless 202 when Supabase isn't configured.
            const early = payload.callSid ? outcomesByCallSidRef.current.get(payload.callSid) : null;
            fetch(`${apiBase}/api/workspace/call-logs`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                sessionId: payload.sessionId, campaignName: sessionMetaRef.current?.listName ?? undefined,
                phone: payload.phone, name: payload.name, company: payload.company, callSid: payload.callSid,
                durationSeconds: payload.durationSeconds, disposition: verdict, transcript: payload.transcript,
                dialedAt: payload.answeredAt ?? undefined, agentOutcome: early?.outcome, notes: early?.notes,
              }),
            }).catch(() => {});
          }
          if (name === 'leg:ended' && payload.phone && onLegEnded) {
            onLegEnded({ phone: payload.phone, disposition: payload.disposition ?? 'UNKNOWN', at: Date.now() });
          }
          if (payload.sessionId && name === 'session:exhausted') {
            finishSession(payload);
          }
          // The line rack: `batch:started` carries every leg for the new
          // batch (replacing whatever the last batch left on screen), and
          // every leg lifecycle event after that upserts just its own leg —
          // real state, not derived from the Activity log's text.
          if (name === 'batch:started') {
            setBatchLegs(payload.legs ?? []);
          } else if (LEG_LIFECYCLE_EVENTS.has(name) && payload.legId) {
            setBatchLegs((prev) => {
              const idx = prev.findIndex((leg) => leg.legId === payload.legId);
              if (idx === -1) return [...prev, payload];
              const next = [...prev];
              next[idx] = { ...next[idx], ...payload };
              return next;
            });
          }
        } catch {
          /* malformed frame — ignore */
        }
      };
      source.addEventListener(name, listener);
      return [name, listener];
    });

    source.onerror = () => {
      // EventSource reconnects on its own; only note a genuine close.
      if (source.readyState === EventSource.CLOSED) pushEvent('warn', 'Activity feed disconnected');
    };

    return () => {
      for (const [name, listener] of bound) source.removeEventListener(name, listener);
      source.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- finishSession/onLegEnded intentionally not in the dep list: re-subscribing on every session start/stop would drop in-flight SSE frames.
  }, [apiBase, pushEvent]);

  // ─────────────────────────────────────────────────────────────── render ────

  const online = status !== AgentStatus.OFFLINE && status !== AgentStatus.ERROR;
  const inCall = Boolean(callInfo);
  // The server is holding the next call for a disposition and we know which call it was for.
  const reviewOpen = Boolean(session?.reviewPending && lastEndedCall && !inCall);

  useEffect(() => {
    onStateChange?.({ online: status === AgentStatus.READY, busy: Boolean(session) || starting });
  }, [status, session, starting, onStateChange]);

  return (
    <div className="tab tab--campaigns">
      {/* SignalWire path only — SimpleUser plays the remote stream through
          this element; the Twilio Voice SDK plays its own audio and never
          touches this ref. Hidden: it's a plumbing element, not UI. */}
      <audio ref={remoteAudioRef} autoPlay hidden />
      {error && (
        <div className="alert alert--error" role="alert">
          {error}
        </div>
      )}

      <section className="panel">
        <div className="panel__head">
          <h2 className="panel__title">Call connection</h2>
          <span className={`badge badge--${STATUS_TONE[status]}`} role="status" aria-live="polite">
            <span className="badge__dot" aria-hidden="true" />
            {status}
          </span>
        </div>
        {bridgeMode && <p className="bridge-notice">Phone bridge active. Call audio rings on your connected phone. Browser mic, speaker, mute and keypad do not control that phone call.</p>}
        <div className="row">
          <button type="button" className="btn btn--primary" onClick={goOnline} disabled={online}>
            Go online
          </button>
          <button type="button" className="btn" onClick={goOffline} disabled={!online || Boolean(session)}>
            Go offline
          </button>
          <label className="toggle">
            <input type="checkbox" checked={autoAnswer} onChange={(e) => setAutoAnswer(e.target.checked)} />
            Auto-answer connected leads
          </label>
        </div>
      </section>

      {settingsOpen && (
        <SettingsPanel
          onClose={onCloseSettings}
          theme={theme}
          onToggleTheme={onToggleTheme}
          apiBase={apiBase}
          authHeaders={authHeaders}
          bridgeMode={bridgeMode}
          currentUser={currentUser}
          accountsEnabled={accountsEnabled}
          audioTab={<>
            <p className="idle__text">{bridgeMode ? 'SignalWire phone bridge: calls ring on the configured agent phone. Browser audio is diagnostic only.'
              : 'Audio check is for this browser. Phone bridge destination and caller ID are configured on the server.'}</p>
            <AudioCheck bridgeMode={bridgeMode} />
          </>}
        />
      )}

      {reviewOpen && (
        <NotesModal
          prospectInfo={lastEndedCall}
          dispositions={dispositions}
          initialNotes={notes}
          saving={savingOutcome}
          error={saveError}
          onSubmit={({ disposition, notes: text }) => logOutcome(disposition.outcome_key, { notes: text, label: disposition.label })}
        />
      )}

      {/* Promoted out of the Dial list panel so the numbers that matter mid-
          shift — how many are left, how many turned into a meeting — are
          visible without scrolling past whatever's on the line right now. */}
      {session && (
        <section className="panel panel--metrics">
          <div className="panel__head">
            <h2 className="panel__title">Live metrics</h2>
            {session.holdRequested && (
              <span className="badge badge--pending">{session.state === 'PAUSED' ? 'Paused' : 'Pausing after this call'}</span>
            )}
          </div>
          <dl className="facts facts--wide">
            <div>
              <dt>Remaining</dt>
              <dd className="mono">{session.remainingLeads}</dd>
            </div>
            <div>
              <dt>Humans</dt>
              <dd className="mono">{session.stats?.humans ?? 0}</dd>
            </div>
            <div>
              <dt>Machines</dt>
              <dd className="mono">{session.stats?.machines ?? 0}</dd>
            </div>
            <div>
              <dt>No answer</dt>
              <dd className="mono">{session.stats?.noAnswer ?? 0}</dd>
            </div>
            <div>
              <dt>Meetings</dt>
              <dd className="mono">{meetingsBooked}</dd>
            </div>
            <div>
              <dt>Conversion</dt>
              <dd className="mono">
                {session.stats?.humans ? `${Math.round((meetingsBooked / session.stats.humans) * 100)}%` : '—'}
              </dd>
            </div>
          </dl>
        </section>
      )}

      {/* The line rack — one tile per leg in the current batch, real state
          off the same SSE feed the Activity log reads. One tile in
          power-dial mode (batchSize forced to 1), up to ten in parallel. */}
      {batchLegs.length > 0 && (
        <section className="panel panel--rack">
          <div className="panel__head">
            <h2 className="panel__title">Lines</h2>
            <span className="pill pill--muted">{batchLegs.length} in this batch</span>
          </div>
          <div className="line-rack">
            {batchLegs.map((leg) => {
              const phase = legPhase(leg);
              const label = leg.company || leg.name || leg.phone;
              return (
                <div key={leg.legId} className={`line-tile ${phase.tone === 'live' ? 'line-tile--connected' : ''}`}>
                  <p className="line-tile__label">{label}</p>
                  {label !== leg.phone && <p className="mono line-tile__phone">{leg.phone}</p>}
                  <span className={`badge badge--${phase.tone}`}>
                    <span className="badge__dot" aria-hidden="true" />
                    {phase.label}
                  </span>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {pendingCall && (
        <section className="panel panel--ringing">
          <h2 className="panel__title">Incoming lead</h2>
          <p className="mono">{pendingCall.parameters?.From ?? 'unknown number'}</p>
          <div className="row">
            <button type="button" className="btn btn--go" onClick={acceptCall}>
              Answer
            </button>
            <button type="button" className="btn btn--danger" onClick={rejectCall}>
              Reject
            </button>
          </div>
        </section>
      )}

      {(inCall || (currentLead && session && !lastEndedCall)) && (
        <section className="panel panel--live">
          <div className="panel__head"><h2 className="panel__title">{inCall ? (bridgeMode ? 'Lead answered · check your phone' : 'On call') : 'Now dialing'}</h2>
            {inCall && <span className="mono notes__timer">{formatDuration(elapsed)}</span>}</div>
          <ProspectPop info={callInfo || (currentLead && { from: currentLead.phone, ...currentLead })} />

          {!bridgeMode && inCall && <div className="meters">
            <Meter label="Mic" value={muted ? 0 : inputLevel} muted={muted} />
            <Meter label="Lead" value={outputLevel} />
          </div>}

          {!bridgeMode && inCall && <><div className="row">
            <button type="button" className={`btn ${muted && !held ? 'btn--warn' : ''}`} onClick={toggleMute} disabled={held}>
              {muted ? 'Unmute' : 'Mute'}
            </button>
            <button type="button" className={`btn ${held ? 'btn--warn' : ''}`} onClick={toggleHold}>
              {held ? 'Resume call' : 'Hold'}
            </button>
            <button type="button" className="btn btn--stop" onClick={hangUp}>
              End call
            </button>
          </div><div className="dial-pad" aria-label="Call keypad">
            {'123456789*0#'.split('').map((digit) => <button key={digit} type="button" className="dial-pad__key"
              disabled={!callRef.current || typeof callRef.current.sendDigits !== 'function'}
              onClick={() => callRef.current?.sendDigits?.(digit)}>{digit}</button>)}
          </div></>}
          {bridgeMode && <p className="idle__text">Use your phone’s keypad, mute and hang-up controls. The browser cannot send tones or carry audio in phone-bridge mode.</p>}
        </section>
      )}

      {!pendingCall && !inCall && !currentLead && (
        <section className="panel panel--idle">
          <h2 className="panel__title">Call</h2>
          <p className="idle__text">{online ? 'Nothing on the line. A connected lead lands here.' : 'Go online to take calls.'}</p>
        </section>
      )}

      {/* Visible for the life of a campaign, not just mid-call, so notes
          taken from the previous call stay reachable while the next one
          rings. Disposition can be logged mid-call as well as after —
          nothing about "the meeting's booked" requires hanging up first. */}
      {session && (
        <section className="panel panel--notes">
          <div className="panel__head">
            <h2 className="panel__title">Call notes</h2>
            {(inCall || lastEndedCall) && (
              <span className="mono notes__timer">
                {inCall ? formatDuration(elapsed) : formatDuration(lastEndedCall.durationSeconds ?? 0)}
              </span>
            )}
          </div>

          {inCall || lastEndedCall ? (
            <>
              <ProspectPop info={inCall ? callInfo : lastEndedCall} />
              <textarea
                className="textarea"
                rows={3}
                placeholder="What came up, next steps…"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
              {!reviewOpen && <div className="row">
                {OUTCOMES.map((o) => (
                  <button
                    key={o.key}
                    type="button"
                    className={`btn ${o.tone === 'good' ? 'btn--primary' : ''}`}
                    onClick={() => logOutcome(o.key)}
                    disabled={savingOutcome || inCall}
                  >
                    {lastEndedCall && !inCall ? `${o.label} · save & continue` : o.label}
                  </button>
                ))}
              </div>}

              <div className="transcript">
                <p className="transcript__label">What we heard before connecting</p>
                {(inCall ? callInfo?.transcript : lastEndedCall?.transcript) ? (
                  <p className="transcript__text">{inCall ? callInfo.transcript : lastEndedCall.transcript}</p>
                ) : (
                  <p className="transcript__empty">
                    Nothing captured here. Live in-call transcription isn't wired up yet — this only ever shows the
                    snippet the classifier heard before the line connected.
                  </p>
                )}
              </div>
            </>
          ) : (
            <p className="idle__text">Waiting for the next call…</p>
          )}
        </section>
      )}

      <section className="panel">
        <div className="panel__head">
          <h2 className="panel__title">Selected lists</h2>
          {activeListName && <span className="pill">{activeListName}</span>}
        </div>
        <p className="idle__text">{activeListName ? `${leadsText.split('\n').filter(Boolean).length} unique number(s) ready. Loading does not place a call.`
          : 'Select one or more dialable lists in the sidebar or Lists tab, then load them here.'}</p>
        <div className="modes" role="group" aria-label="Dialing mode">
          <button
            type="button"
            className={`mode ${mode === 'power' ? 'mode--on' : ''}`}
            aria-pressed={mode === 'power'}
            onClick={() => setMode('power')}
            disabled={Boolean(session)}
          >
            <span className="mode__name">Power dial</span>
            <span className="mode__note">One line. You hear every call.</span>
          </button>
          <button
            type="button"
            className={`mode ${mode === 'parallel' ? 'mode--on' : ''}`}
            aria-pressed={mode === 'parallel'}
            onClick={() => setMode('parallel')}
            disabled={Boolean(session)}
          >
            <span className="mode__name">Parallel dial</span>
            <span className="mode__note">Several lines. Voicemails screened out.</span>
          </button>
        </div>

        <div className="row">
          <label className="field">
            Lines per batch
            <input
              type="number"
              min={1}
              max={10}
              value={screening ? batchSize : 1}
              onChange={(e) => setBatchSize(e.target.value)}
              disabled={Boolean(session) || !screening}
            />
          </label>
          {session ? (
            // Red the moment dialing begins. In a browser call it hangs up the
            // live call; otherwise (or in phone-bridge mode, where only the
            // phone can hang up) it ends the whole session.
            <button type="button" className="btn btn--stop" onClick={inCall && !bridgeMode ? hangUp : stopSession}>
              {inCall && !bridgeMode ? 'End call' : 'End session'}
            </button>
          ) : (
            <button
              type="button"
              className="btn btn--go"
              onClick={startSession}
              disabled={!online || starting || !leadsText.trim()}
            >
              {starting ? 'Starting…' : 'Start dialing'}
            </button>
          )}
          {session && (
            <button type="button" className={`btn ${session.holdRequested ? 'btn--warn' : ''}`} onClick={togglePause}>
              {session.holdRequested ? 'Resume session' : 'Pause session'}
            </button>
          )}
          {session && inCall && !bridgeMode && (
            <button type="button" className="btn btn--danger" onClick={stopSession}>
              End session
            </button>
          )}
        </div>
      </section>

      <section className="panel">
        <div className="panel__head">
          <h2 className="panel__title">Activity</h2>
          <button type="button" className="btn btn--ghost" onClick={() => setEvents([])}>
            Clear
          </button>
        </div>
        <ul className="log">
          {events.length === 0 && <li className="log__empty">Nothing yet.</li>}
          {events.map((entry) => (
            <li key={entry.id} className={`log__row log__row--${entry.level}`}>
              <time className="log__time mono">{entry.at.toLocaleTimeString()}</time>
              <span className="log__msg">{entry.message}</span>
              {entry.detail && <span className="log__detail mono">{entry.detail}</span>}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

/** The screen-pop: company/name when the number matches a known contact,
 * the bare phone number when it doesn't. */
function ProspectPop({ info }) {
  if (!info) return <p className="mono outcome__number">—</p>;
  if (!info.company && !info.name) {
    return <p className="mono outcome__number">{info.from ?? '—'}</p>;
  }
  return (
    <div className="prospect">
      {info.company && <p className="prospect__company">{info.company}</p>}
      <p className="prospect__meta">
        {info.name && <span className="prospect__name">{info.name}</span>}
        <span className="mono prospect__phone">{info.from ?? '—'}</span>
      </p>
      {info.title && <p className="prospect__detail">{info.title}</p>}
      {info.email && <p className="prospect__detail">{info.email}</p>}
      {info.companyUrl && <p className="prospect__detail">Website: {info.companyUrl}</p>}
    </div>
  );
}

/** Simple level meter for mic / far-end audio. */
function Meter({ label, value, muted = false }) {
  const percent = Math.min(100, Math.round(value * 100));
  return (
    <div className="meter">
      <span className="meter__label">{label}</span>
      <div className="meter__track" role="meter" aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100}>
        <div className={`meter__fill ${muted ? 'meter__fill--muted' : ''}`} style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
}

/** Local browser diagnostic only. In phone-bridge mode the phone carries the
 * conversation; a responsive meter here does not imply live call audio. */
function AudioCheck({ bridgeMode }) {
  const [inputs, setInputs] = useState([]);
  const [outputs, setOutputs] = useState([]);
  const [micId, setMicId] = useState('');
  const [level, setLevel] = useState(0);
  const [message, setMessage] = useState('Run a check to allow the microphone and see the available devices.');
  const stopRef = useRef(null);
  const [testing, setTesting] = useState(false);

  useEffect(() => () => stopRef.current?.(), []);

  async function checkMic(deviceId = micId) {
    stopRef.current?.();
    setTesting(false);
    if (!navigator.mediaDevices?.getUserMedia) {
      setMessage('This browser does not support microphone testing.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: deviceId ? { deviceId: { ideal: deviceId } } : true });
      const devices = await navigator.mediaDevices.enumerateDevices();
      setInputs(devices.filter((device) => device.kind === 'audioinput'));
      setOutputs(devices.filter((device) => device.kind === 'audiooutput'));
      const context = new AudioContext();
      const analyser = context.createAnalyser();
      analyser.fftSize = 256;
      context.createMediaStreamSource(stream).connect(analyser);
      const bins = new Uint8Array(analyser.frequencyBinCount);
      const timer = setInterval(() => {
        analyser.getByteFrequencyData(bins);
        setLevel(Math.min(1, bins.reduce((sum, n) => sum + n, 0) / bins.length / 80));
      }, 100);
      stopRef.current = () => { clearInterval(timer); stream.getTracks().forEach((track) => track.stop()); context.close().catch(() => {}); setLevel(0); };
      setTesting(true);
      setMessage(bridgeMode ? 'Browser mic test only — your phone carries live calls.' : 'Speak to check the browser microphone.');
    } catch (err) {
      setMessage(`Microphone unavailable: ${err.message}`);
    }
  }

  async function testSpeaker() {
    try {
      const context = new AudioContext();
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      gain.gain.value = 0.08;
      oscillator.frequency.value = 660;
      oscillator.connect(gain).connect(context.destination);
      oscillator.start();
      oscillator.stop(context.currentTime + 0.3);
      oscillator.onended = () => context.close().catch(() => {});
      setMessage('Test tone played on your browser’s default output.');
    } catch (err) { setMessage(`Speaker test unavailable: ${err.message}`); }
  }

  return <div className="audio-check">
    <div className="audio-check__top"><strong>Browser audio check</strong><span>{bridgeMode ? 'Diagnostics only · calls on phone' : 'Check before dialing'}</span></div>
    <div className="row">
      <button type="button" className="btn" onClick={() => checkMic()}>Test microphone</button>
      {testing && <button type="button" className="btn" onClick={() => { stopRef.current?.(); stopRef.current = null; setTesting(false); setMessage('Microphone test stopped.'); }}>Stop test</button>}
      <button type="button" className="btn" onClick={testSpeaker}>Play test tone</button>
    </div>
    {inputs.length > 0 && <label className="field">Microphone
      <select className="select" value={micId} onChange={(event) => { setMicId(event.target.value); checkMic(event.target.value); }}>
        <option value="">System default</option>
        {inputs.map((device) => <option key={device.deviceId} value={device.deviceId}>{device.label || 'Microphone'}</option>)}
      </select></label>}
    {outputs.length > 0 && <p className="audio-check__message">Detected output: {outputs.map((device) => device.label || 'Speaker').join(', ')}. Test tone uses the system default.</p>}
    <Meter label="Mic test" value={level} />
    <p className="audio-check__message" role="status">{message}</p>
  </div>;
}

function formatDuration(totalSeconds) {
  const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
  const seconds = String(totalSeconds % 60).padStart(2, '0');
  return `${minutes}:${seconds}`;
}
